import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useHospitalAuth } from './HospitalAuthContext';
import { apiRequest, API_BASE_URL } from '../config/api';

const HospitalBrandingContext = createContext(undefined);

// Helper function to extract hospital slug from URL pathname
const extractHospitalSlugFromPath = (pathname) => {
  // Match patterns like /:hospitalSlug, /:hospitalSlug/, /:hospitalSlug/patient/login, etc.
  // The regex matches: /slug or /slug/ or /slug/anything
  const match = pathname.match(/^\/([^/]+)(?:\/|$)/);
  if (match && match[1]) {
    const slug = match[1];
    // Exclude known non-slug paths
    const excludedPaths = ['admin', 'doctor', 'patient', 'login', 'register', 'pricing', 'api'];
    if (!excludedPaths.includes(slug.toLowerCase())) {
      return slug;
    }
  }
  return null;
};

export const HospitalBrandingProvider = ({ children }) => {
  const { hospital, refreshHospitalStatus } = useHospitalAuth();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pathname, setPathname] = useState(window.location.pathname);
  
  // Listen to pathname changes using popstate and pushstate events
  useEffect(() => {
    const updatePathname = () => {
      setPathname(window.location.pathname);
    };
    
    // Initial pathname
    updatePathname();
    
    // Listen to popstate (back/forward navigation)
    window.addEventListener('popstate', updatePathname);
    
    // Listen to pushstate/replacestate (programmatic navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      // Use setTimeout to ensure pathname is updated after navigation
      setTimeout(updatePathname, 0);
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      // Use setTimeout to ensure pathname is updated after navigation
      setTimeout(updatePathname, 0);
    };
    
    // Also listen to React Router navigation via custom event
    const handleRouteChange = () => {
      setTimeout(updatePathname, 0);
    };
    
    window.addEventListener('routechange', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', updatePathname);
      window.removeEventListener('routechange', handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);
  
  // Extract hospital slug from URL pathname
  const hospitalSlug = extractHospitalSlugFromPath(pathname);
  
  // Debug logging
  React.useEffect(() => {
    console.log('HospitalBrandingContext: Pathname:', pathname, 'Extracted slug:', hospitalSlug, 'Hospital from auth:', hospital?.name);
  }, [pathname, hospitalSlug, hospital]);

  const loadBranding = useCallback(async () => {
    // If hospital is available from auth context, use it
    if (hospital) {
      try {
        const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${hospital.id}`);
      
      if (statusData.success) {
        // Normalize logo URL - convert to use image proxy endpoint for CORS support
        let logoUrl = statusData.hospital.logo_url;
        if (logoUrl) {
          // Check if it's a full URL
          if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
            // Extract the path from the full URL
            try {
              const urlObj = new URL(logoUrl);
              // Check if it's pointing to our own server (localhost or same origin)
              const currentHostname = window.location.hostname;
              const isOurServer = urlObj.hostname === 'localhost' || 
                                 urlObj.hostname === '127.0.0.1' ||
                                 urlObj.hostname === currentHostname ||
                                 urlObj.hostname.endsWith('.' + currentHostname);
              
              if (isOurServer) {
                // Extract path and use proxy endpoint
                let path = urlObj.pathname;
                // Remove leading slash
                path = path.startsWith('/') ? path.substring(1) : path;
                // Remove 'backend/' or 'api/' prefix if present
                path = path.replace(/^(backend|api)\//, '');
                // Use the serve_image.php endpoint
                logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(path)}`;
              } else {
                // External URL, use as-is but fix double slashes
                logoUrl = logoUrl.replace(/([^:]\/)\/+/g, '$1');
              }
            } catch (e) {
              // Invalid URL, try to extract path manually
              const pathMatch = logoUrl.match(/\/uploads\/[^?]*/);
              if (pathMatch) {
                let path = pathMatch[0].substring(1); // Remove leading slash
                logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(path)}`;
              }
            }
          } else {
            // For relative paths, use the image proxy endpoint to ensure CORS headers
            // Remove leading slash if present
            let cleanPath = logoUrl.startsWith('/') ? logoUrl.substring(1) : logoUrl;
            // Remove 'backend/' or 'api/' prefix if present
            cleanPath = cleanPath.replace(/^(backend|api)\//, '');
            // Use the serve_image.php endpoint
            logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(cleanPath)}`;
          }
        }
        
        setBranding({
          name: statusData.hospital.name,
          logoUrl: logoUrl,
          landingPageUrl: statusData.hospital.landing_page_url,
          address: statusData.hospital.address,
          phone: statusData.hospital.phone,
          email: statusData.hospital.email,
          slug: statusData.hospital.slug
        });
      }
    } catch (error) {
      console.error('Failed to load hospital branding:', error);
      // Use fallback branding from hospital context
      // Normalize logo URL - convert to use image proxy endpoint for CORS support
      let logoUrl = hospital.logo_url || null;
      if (logoUrl) {
        // Check if it's a full URL
        if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
          // Extract the path from the full URL
          try {
            const urlObj = new URL(logoUrl);
            // Check if it's pointing to our own server (localhost or same origin)
            const currentHostname = window.location.hostname;
            const isOurServer = urlObj.hostname === 'localhost' || 
                               urlObj.hostname === '127.0.0.1' ||
                               urlObj.hostname === currentHostname ||
                               urlObj.hostname.endsWith('.' + currentHostname);
            
            if (isOurServer) {
              // Extract path and use proxy endpoint
              let path = urlObj.pathname;
              // Remove leading slash
              path = path.startsWith('/') ? path.substring(1) : path;
              // Remove 'backend/' or 'api/' prefix if present
              path = path.replace(/^(backend|api)\//, '');
              // Use the serve_image.php endpoint
              logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(path)}`;
            } else {
              // External URL, use as-is but fix double slashes
              logoUrl = logoUrl.replace(/([^:]\/)\/+/g, '$1');
            }
          } catch (e) {
            // Invalid URL, try to extract path manually
            const pathMatch = logoUrl.match(/\/uploads\/[^?]*/);
            if (pathMatch) {
              let path = pathMatch[0].substring(1); // Remove leading slash
              logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(path)}`;
            }
          }
        } else {
          // For relative paths, use the image proxy endpoint to ensure CORS headers
          // Remove leading slash if present
          let cleanPath = logoUrl.startsWith('/') ? logoUrl.substring(1) : logoUrl;
          // Remove 'backend/' or 'api/' prefix if present
          cleanPath = cleanPath.replace(/^(backend|api)\//, '');
          // Use the serve_image.php endpoint
          logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(cleanPath)}`;
        }
      }
      
      setBranding({
        name: hospital.name,
        logoUrl: logoUrl,
        landingPageUrl: hospital.landing_page_url || null,
        address: null,
        phone: null,
        email: hospital.email,
        slug: hospital.slug
      });
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // If no hospital from auth context, try to load from URL slug (for patient login page, etc.)
    if (hospitalSlug) {
      try {
        setLoading(true);
        console.log('HospitalBrandingContext: Loading branding from slug:', hospitalSlug);
        // Use get_hospital_by_slug.php to get hospital info by slug
        const hospitalData = await apiRequest(`get_hospital_by_slug.php?slug=${hospitalSlug}`);
        console.log('HospitalBrandingContext: API response:', hospitalData);
        
        if (hospitalData.success && hospitalData.hospital) {
          const hospitalInfo = hospitalData.hospital;
          console.log('HospitalBrandingContext: Hospital info:', hospitalInfo);
          
          // Normalize logo URL - convert to use image proxy endpoint for CORS support
          let logoUrl = hospitalInfo.logo_url;
          if (logoUrl) {
            // Check if it's a full URL
            if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
              // Extract the path from the full URL
              try {
                const urlObj = new URL(logoUrl);
                // Check if it's pointing to our own server (localhost or same origin)
                const currentHostname = window.location.hostname;
                const isOurServer = urlObj.hostname === 'localhost' || 
                                 urlObj.hostname === '127.0.0.1' ||
                                 urlObj.hostname === currentHostname ||
                                 urlObj.hostname.endsWith('.' + currentHostname);
                
                if (isOurServer) {
                  // Extract path and use proxy endpoint
                  let path = urlObj.pathname;
                  // Remove leading slash
                  path = path.startsWith('/') ? path.substring(1) : path;
                  // Remove 'backend/' or 'api/' prefix if present
                  path = path.replace(/^(backend|api)\//, '');
                  // Use the serve_image.php endpoint
                  logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(path)}`;
                } else {
                  // External URL, use as-is but fix double slashes
                  logoUrl = logoUrl.replace(/([^:]\/)\/+/g, '$1');
                }
              } catch (e) {
                // Invalid URL, try to extract path manually
                const pathMatch = logoUrl.match(/\/uploads\/[^?]*/);
                if (pathMatch) {
                  let path = pathMatch[0].substring(1); // Remove leading slash
                  logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(path)}`;
                }
              }
            } else {
              // For relative paths, use the image proxy endpoint to ensure CORS headers
              // Remove leading slash if present
              let cleanPath = logoUrl.startsWith('/') ? logoUrl.substring(1) : logoUrl;
              // Remove 'backend/' or 'api/' prefix if present
              cleanPath = cleanPath.replace(/^(backend|api)\//, '');
              // Use the serve_image.php endpoint
              logoUrl = `${API_BASE_URL}/serve_image.php?path=${encodeURIComponent(cleanPath)}`;
            }
          }
          
          const brandingData = {
            name: hospitalInfo.name,
            logoUrl: logoUrl,
            landingPageUrl: hospitalInfo.landing_page_url,
            address: hospitalInfo.address,
            phone: hospitalInfo.phone,
            email: hospitalInfo.email,
            slug: hospitalInfo.slug
          };
          console.log('HospitalBrandingContext: Setting branding data:', brandingData);
          setBranding(brandingData);
        } else {
          console.warn('HospitalBrandingContext: Hospital data not found or invalid:', hospitalData);
          setBranding(null);
        }
      } catch (error) {
        console.error('HospitalBrandingContext: Failed to load hospital branding from slug:', error);
        setBranding(null);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // No hospital and no slug - set branding to null
    console.log('HospitalBrandingContext: No hospital and no slug, setting branding to null. Pathname:', pathname, 'Extracted slug:', hospitalSlug);
    setBranding(null);
    setLoading(false);
  }, [hospital, hospitalSlug, pathname]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const refreshBranding = useCallback(() => {
    setLoading(true);
    loadBranding();
  }, [loadBranding]);

  const value = {
    branding,
    loading,
    refreshBranding,
    // Helper functions
    getHospitalName: () => branding?.name || 'Dental Hub',
    getLogoUrl: () => branding?.logoUrl || null,
    getLandingPageUrl: () => branding?.landingPageUrl || null,
    getAddress: () => branding?.address || null,
    getPhone: () => branding?.phone || null,
    getEmail: () => branding?.email || null
  };

  return (
    <HospitalBrandingContext.Provider value={value}>
      {children}
    </HospitalBrandingContext.Provider>
  );
};

export const useHospitalBranding = () => {
  const context = useContext(HospitalBrandingContext);
  if (context === undefined) {
    throw new Error('useHospitalBranding must be used within a HospitalBrandingProvider');
  }
  return context;
};

